/**
 * L'AREA CLIENTE: quattro pagine, e non è il gestionale con meno voci.
 *
 * Un cliente non deve navigare il gestionale, **deve vedere i suoi soldi**. Il
 * backend glielo impone già — `lib/client-scope.ts` è una allowlist di quattro
 * rotte e 403 su tutto il resto — ma fino a qui l'interfaccia gli mostrava la
 * barra dell'operatore: Conti, Clienti, Server, Prop firm, Operazione, Fasi,
 * Eventi. Sette voci su cui poteva solo prendere un rifiuto.
 *
 * **Un menu di porte chiuse non è un menu**, ed è peggio di un menu corto: la
 * prima volta che ci prova, chi lo usa crede di aver rotto qualcosa — o, peggio,
 * capisce che esistono pagine che gli nascondiamo, che è esattamente
 * l'impressione da non dare a chi ci affida dei soldi.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { EmptyState, PageHeader } from "@/components/Layout";
import { ErrorNote, useCurrentUser } from "@/components/ui";
import { money, date } from "@/lib/format";
import { KIND_LABEL } from "@/components/ledger";
import type { ClientAccount } from "@/lib/types";
import { lazy, Suspense } from "react";

/*
 * Il riepilogo porta `lightweight-charts`: si carica a parte, come il grafico
 * dei livelli, e il bundle che scaricano tutti — operatori compresi — non cresce.
 */
const ClientSummary = lazy(() => import("@/components/client-summary").then((m) => ({ default: m.ClientSummary })));

// ---------------------------------------------------------------------------
// I miei conti
// ---------------------------------------------------------------------------

/**
 * Prop e broker hanno un colore costante in tutta l'applicazione, e qui vale
 * come altrove: indaco il prop (capitale prestato), ambra il broker (soldi del
 * cliente). È l'informazione che va letta per prima.
 */
function RoleTag({ role }: { role: ClientAccount["role"] }) {
  const prop = role === "PROP";
  return (
    <span className={`rounded px-1.5 py-0.5 text-2xs font-medium ${prop ? "bg-prop/15 text-prop" : "bg-broker/15 text-broker"}`}>
      {prop ? "Prop firm" : "Broker"}
    </span>
  );
}

export function MyAccounts() {
  const q = useQuery({ queryKey: ["me", "accounts"], queryFn: () => api.myAccounts() });
  const conti = q.data?.accounts ?? [];

  return (
    <div>
      <PageHeader title="I miei conti" subtitle="Saldo e stato dei conti collegati al tuo nome." />

      {q.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        </div>
      ) : !conti.length ? (
        <EmptyState
          title="Nessun conto collegato"
          hint="Quando il tuo gestore collega un conto al tuo nome, lo vedi qui con il suo saldo."
        />
      ) : (
        <div className="grid gap-4 px-6 py-5 sm:grid-cols-2">
          {conti.map((c: ClientAccount) => (
            <div key={c.login} className="rounded-card border border-line bg-surface p-4">
              <div className="flex items-center gap-2">
                <RoleTag role={c.role} />
                <span className="truncate text-sm text-muted">{c.holder}</span>
                <span className={`ml-auto text-2xs ${c.connected ? "text-muted" : "text-faint"}`}>
                  {c.connected ? "collegato" : "non collegato"}
                </span>
              </div>
              {/* Monospazio: il numero di conto si confronta carattere per carattere. */}
              <p className="mt-2 font-mono text-xs text-faint">{c.login}</p>
              <dl className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-xs text-faint">Saldo</dt>
                  <dd className="mt-0.5 text-lg tabular-nums text-ink">{money(c.balance, c.currency)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-faint">Equity</dt>
                  <dd className="mt-0.5 text-lg tabular-nums text-ink">{money(c.equity, c.currency)}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// I miei movimenti
// ---------------------------------------------------------------------------

export function MyLedger() {
  const user = useCurrentUser();
  const clientId = user.clientId;
  const q = useQuery({
    queryKey: ["me", "transactions", clientId],
    queryFn: () => api.ledger.list(clientId!),
    enabled: !!clientId,
  });
  const rows = q.data?.rows ?? [];

  /*
   * Un utente CLIENT senza `clientId` non dovrebbe esistere — si crea sempre
   * legato a un cliente — ma se esistesse, chiedere `/clients/undefined/...`
   * darebbe un errore che non spiega niente. Meglio dirlo.
   */
  if (!clientId) {
    return (
      <div>
        <PageHeader title="Movimenti" />
        <div className="px-6 py-6">
          <ErrorNote>Questo accesso non è legato a un cliente: scrivi al tuo gestore.</ErrorNote>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Movimenti" subtitle="Il tuo registro: versamenti, prelievi, quote e rettifiche." />

      {q.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        </div>
      ) : !rows.length ? (
        <EmptyState title="Nessun movimento" hint="Qui compaiono i movimenti del tuo registro, dal più recente." />
      ) : (
        <div className="overflow-x-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="pb-2 font-medium">Data</th>
                <th className="pb-2 font-medium">Movimento</th>
                <th className="pb-2 font-medium">Nota</th>
                <th className="pb-2 text-right font-medium">Importo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-line/60">
                  <td className="py-2.5 tabular-nums text-muted">{date(r.occurredAt)}</td>
                  <td className="py-2.5 text-ink">{KIND_LABEL[r.kind] ?? r.kind}</td>
                  <td className="py-2.5 text-muted">{r.notes ?? ""}</td>
                  <td className={`py-2.5 text-right tabular-nums ${Number(r.amount) < 0 ? "text-loss" : "text-ink"}`}>
                    {money(r.amount, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home dell'area cliente
// ---------------------------------------------------------------------------

/**
 * La prima cosa che un cliente vede entrando: in cima il RIEPILOGO — quanto gli
 * è spettato da payout e bruciature, e quanto manca alle sue fasi attive (punto
 * 20, `components/client-summary.tsx`, numeri tutti dal backend) — poi i suoi
 * conti, e da lì dove andare.
 */
export function MyHome() {
  return (
    <div>
      <Suspense fallback={<p className="px-6 py-6 text-sm text-muted">Caricamento…</p>}>
        <ClientSummary />
      </Suspense>
      <MyAccounts />
      <div className="flex flex-wrap gap-4 px-6 pb-8 text-sm">
        <Link to="/cicli" className="text-muted hover:text-ink hover:underline">
          I miei cicli →
        </Link>
        <Link to="/movimenti" className="text-muted hover:text-ink hover:underline">
          I miei movimenti →
        </Link>
      </div>
    </div>
  );
}
